import React, { useState, useContext, useRef } from 'react'
import { FormProvider } from 'react-hook-form'
import cn from 'classnames'
import { Button, ButtonStyles, Typography, IconSpinner } from '@grammarly/badger-ui'
import { UploadButtonError } from '@grammarly/website-components'

import { TextInput } from '../../../inputs/text-input'
import { ExpandedTextarea } from '../expanded-text-area'
import { ProgressBar } from '../progress-bar'
import { Output } from '../output'
import { FileUploadOrTextInputTabs } from '../file-upload-or-text-input-tabs'

import { gnarSpec } from '../../../../lib/tracking/gnar'
import { PageContext } from '../../../../contexts/page-context'
import { TypeComponentTextInput } from '../../../../lib/types/component-text-input'
import { FormProvider as UILibFormProvider } from '../../../ui-lib/form/form-context'
import { replaceSpacesWithDashes } from '../../../../lib/utils/strings'
import { RequiredOrOptional } from '../../../ui-lib/form/form-context'
import { SubmitButtonContent } from '../shared-components/submit-button-content'
import { errorStyle } from '../../../ui-lib/form/error-text'
import { AnonymousTextLimitModal } from '../anonymous-text-limit-modal'
import { useGoTemplates } from '../go-templates-context'

import styles from './form.module.scss'

export const AnnounceLoading: React.FC<{ isLoading: boolean }> = ({ isLoading }) => (
  <span aria-live="assertive" className={styles.srOnly}>
    {isLoading && 'Loading...'}
  </span>
)

interface FormProps {
  questions: TypeComponentTextInput[]
}

export const Form: React.FC<FormProps> = ({ questions }) => {
  const {
    methods,
    output,
    shouldTriggerWall,
    cheetahResultState,
    submitButtonText,
    loadingButtonText,
    outputLabel,
    outputDescription,
    internalName,
    isWallOpen,
    onFormChangeCapture,
    onFormClickCapture,
    updatePastedPerStep,
    trackFormStepComplete,
    getOnSubmit,
    onChange,
  } = useGoTemplates()

  const { handleSubmit, trigger } = methods
  const { pageId, slug } = useContext(PageContext)
  const hasUploadedFilePerStep = useRef(false)

  const steps = questions.length
  const formId = `form-multi-step-${replaceSpacesWithDashes(internalName)}`
  const [step, setStep] = useState(0)
  const [activeTab, setActiveTab] = useState(0)

  const progressBarLabel = `progressbar-label-${replaceSpacesWithDashes(internalName)}`

  const getFormStepTrackingData = (): {
    activeStep: number
    totalSteps: number
    inputName: string
    inputType: 'file upload' | 'long text' | 'short text'
    isInputRequired: boolean
  } => {
    const currentQuestion = questions[step]
    const { hasLongformText, name, isRequired } = currentQuestion.fields
    const inputType = hasUploadedFilePerStep.current ? 'file upload' : hasLongformText ? 'long text' : 'short text'

    return { activeStep: step + 1, totalSteps: steps, inputName: name, inputType, isInputRequired: !!isRequired }
  }

  const onFileUploadError = (error: UploadButtonError, fileName?: string) => {
    const { activeStep, totalSteps, inputName, inputType } = getFormStepTrackingData()
    const fileExt = fileName?.includes('.') ? fileName.split('.').pop() : undefined
    const errorMessageWithFileExt = `${error}${fileExt ? ' ' + fileExt : ''}`

    gnarSpec.goToolsFormError(activeStep, errorMessageWithFileExt, inputName, inputType, pageId, slug, totalSteps)
  }

  const checkKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      // For longform text, i.e. textarea, pressing enter should move to the next line and not submit the form.
      if (questions[step].fields.hasLongformText) {
        e.stopPropagation()
      } else if (step < steps - 1) {
        // For non-longform text, i.e. input, pressing enter should not submit the form and move to the next step.
        e.preventDefault()
        handleContinue()
      }
    }
  }

  const handleContinue = async () => {
    if (shouldTriggerWall) {
      return
    }

    const { inputName } = getFormStepTrackingData()
    const isInputValid = await trigger(inputName)

    if (isInputValid) {
      trackFormStepComplete(getFormStepTrackingData())
      hasUploadedFilePerStep.current = false
      updatePastedPerStep(false)
      setStep(step + 1)
    }
  }

  const handleBack = () => {
    const { activeStep, totalSteps, inputName, inputType } = getFormStepTrackingData()
    gnarSpec.goToolsFormStepBack(activeStep, inputName, inputType, pageId, slug, totalSteps)

    setStep(step - 1)
  }

  const onSubmit = getOnSubmit(getFormStepTrackingData(), false)

  const isLoading = cheetahResultState === 'loading'

  return (
    <>
      <ProgressBar
        step={step}
        steps={steps}
        isComplete={cheetahResultState === 'done'}
        isStreamingResults={cheetahResultState === 'streaming'}
        progressBarLabel={progressBarLabel}
      />
      <div aria-live="polite">
        {output ? (
          <Output output={output} label={outputLabel} description={outputDescription} />
        ) : (
          <FormProvider {...methods}>
            <UILibFormProvider value={{ formId, requiredOrOptionalIndicator: RequiredOrOptional.Optional }}>
              <form
                id={formId}
                className={cn('font-family-inter', styles.form)}
                onSubmit={handleSubmit(onSubmit)}
                onKeyDown={checkKeyDown}
                aria-live="polite"
                aria-relevant="additions"
                onChangeCapture={shouldTriggerWall ? onFormChangeCapture : undefined}
                onClickCapture={shouldTriggerWall ? onFormClickCapture : undefined}
                method="post"
              >
                {questions.map((s, i) => {
                  const showStep = step === i

                  return (
                    <React.Fragment key={s.fields.name}>
                      <div className={cn(styles.step, showStep && styles.currentStep)}>
                        {s.fields.allowFileUpload ? (
                          <FileUploadOrTextInputTabs
                            activeTab={activeTab}
                            onTabChange={(index) => setActiveTab(index)}
                            onFileUploadSuccess={() => {
                              hasUploadedFilePerStep.current = true
                            }}
                            onFileUploadError={onFileUploadError}
                            onRemoveFile={() => {
                              hasUploadedFilePerStep.current = false
                            }}
                            onPasteTextInput={() => updatePastedPerStep(true)}
                            textareaClassName={styles.textField}
                            helperTextClassName={styles.helperText}
                            labelClassName={styles.label}
                            structure={s}
                            isDisabled={isLoading}
                            onChange={onChange}
                          />
                        ) : s.fields.hasLongformText ? (
                          <ExpandedTextarea
                            structure={s}
                            isShown={showStep}
                            className={cn(styles.textField, styles.textarea)}
                            labelClassName={styles.label}
                            helperTextClassName={styles.helperText}
                            onPaste={() => updatePastedPerStep(true)}
                            onChange={onChange}
                            isDisabled={isLoading}
                          />
                        ) : (
                          <TextInput
                            structure={s}
                            className={styles.textField}
                            labelClassName={styles.label}
                            helperTextClassName={styles.helperText}
                            onPaste={() => updatePastedPerStep(true)}
                            onChange={onChange}
                            isDisabled={isLoading}
                          />
                        )}
                      </div>
                      <AnonymousTextLimitModal name={s.fields.name} isSignUpWallOpen={isWallOpen} structure={s} />
                    </React.Fragment>
                  )
                })}

                <div className={styles.buttonContainer}>
                  {step === steps - 1 && (
                    <Button
                      padding="compact"
                      className={cn(ButtonStyles.BlueButtonWhiteText, isLoading && styles.submitButtonLoading)}
                      type="submit"
                      iconRight={isLoading && <IconSpinner />}
                      disabled={isLoading}
                    >
                      <SubmitButtonContent
                        isLoading={isLoading}
                        loadingButtonText={loadingButtonText}
                        submitButtonText={submitButtonText}
                      />
                    </Button>
                  )}

                  {step < steps - 1 ? (
                    <Button
                      padding="compact"
                      className={cn(ButtonStyles.BlueButtonWhiteText)}
                      onClick={handleContinue}
                      disabled={cheetahResultState === 'error'}
                    >
                      Continue
                    </Button>
                  ) : null}
                  {step !== 0 && step < steps ? (
                    <Button
                      padding="compact"
                      className={cn(ButtonStyles.WhiteButtonBlueTextWithBorder)}
                      onClick={handleBack}
                      status={isLoading ? 'disabled' : 'enabled'}
                    >
                      Back
                    </Button>
                  ) : null}
                </div>
                <AnnounceLoading isLoading={isLoading} />
                {cheetahResultState === 'error' && (
                  <Typography.C4 as="span" className={errorStyle}>
                    Something went wrong. Please try again later.
                  </Typography.C4>
                )}
              </form>
            </UILibFormProvider>
          </FormProvider>
        )}
      </div>
    </>
  )
}
